import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@shared/components/ui/dialog";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";
import { supabase } from "@lib/supabase/client";
import { toast } from "sonner";
import { Upload, X, FileText, Image, Loader2, Palette, Globe, Send, Info } from "lucide-react";

interface CustomDesignRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  specialistName: string;
  clinicName: string;
  email: string;
  phone: string;
}

const CustomDesignRequestDialog = ({
  open,
  onOpenChange,
  clinicId,
  specialistName,
  clinicName,
  email,
  phone,
}: CustomDesignRequestDialogProps) => {
  const [primaryColor, setPrimaryColor] = useState("#8C222E");
  const [secondaryColor, setSecondaryColor] = useState("#ffffff");
  const [referenceWebsite, setReferenceWebsite] = useState("");
  const [designNotes, setDesignNotes] = useState("");
  const [attachments, setAttachments] = useState<{ url: string; name: string; type: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const isImage = file.type.startsWith("image/");
        const isPdf = file.type === "application/pdf";
        if (!isImage && !isPdf) {
          toast.error(`نوع الملف غير مدعوم: ${file.name}. يرجى رفع صور أو ملفات PDF فقط.`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`الملف ${file.name} أكبر من 10 ميجابايت`);
          continue;
        }

        const fileExt = file.name.split(".").pop();
        const filePath = `design-requests/${clinicId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("clinic-images")
          .upload(filePath, file);

        if (uploadError) {
          toast.error(`فشل رفع الملف: ${file.name}`);
          continue;
        }

        const { data: urlData } = supabase.storage
          .from("clinic-images")
          .getPublicUrl(filePath);

        setAttachments((prev) => [
          ...prev,
          {
            url: urlData.publicUrl,
            name: file.name,
            type: isImage ? "image" : "pdf",
          },
        ]);
      }
    } catch (err) {
      toast.error("حدث خطأ أثناء رفع الملفات");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!designNotes.trim()) {
      toast.error("يرجى كتابة ملاحظات التصميم المطلوب");
      return;
    }

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error("يرجى تسجيل الدخول");
        return;
      }

      // Build full description with structured design preferences
      const fullDescription = [
        `اللون الأساسي: ${primaryColor}`,
        `اللون الثانوي: ${secondaryColor}`,
        referenceWebsite ? `موقع مرجعي: ${referenceWebsite}` : null,
        `ملاحظات التصميم:\n${designNotes.trim()}`,
      ].filter(Boolean).join("\n\n");

      // Save the design request without payment
      const { error: insertError } = await supabase
        .from("clinic_custom_design_requests" as never)
        .insert({
          clinic_id: clinicId,
          specialist_id: session.user.id,
          specialist_name: specialistName,
          clinic_name: clinicName,
          email: email,
          phone: phone,
          description: fullDescription,
          attachments: attachments.map((a) => a.url),
          price: 0,
          payment_status: 'unpaid',
        } as never);

      if (insertError) throw insertError;

      toast.success("تم إرسال طلب التصميم بنجاح! سيتم التواصل معك لتحديد السعر.");
      onOpenChange(false);
      // Reset form
      setPrimaryColor("#8C222E");
      setSecondaryColor("#ffffff");
      setReferenceWebsite("");
      setDesignNotes("");
      setAttachments([]);
    } catch (err) {
      console.error("Error submitting design request:", err);
      toast.error("حدث خطأ أثناء إرسال الطلب");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-xl">طلب تصميم مخصص</DialogTitle>
          <DialogDescription>
            حدد تفضيلاتك البصرية وأرسل طلبك — سيتم تحديد السعر بناءً على متطلبات التصميم
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Auto-filled fields - read only */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">اسم الأخصائي</Label>
              <Input value={specialistName} readOnly className="bg-muted/50 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">اسم العيادة</Label>
              <Input value={clinicName} readOnly className="bg-muted/50 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">البريد الإلكتروني</Label>
              <Input value={email} readOnly className="bg-muted/50 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">رقم الجوال</Label>
              <Input value={phone || "غير محدد"} readOnly className="bg-muted/50 text-sm" />
            </div>
          </div>

          {/* Color Preferences */}
          <div>
            <Label className="flex items-center gap-1.5 mb-2">
              <Palette className="h-4 w-4" />
              الألوان المفضلة
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-md border cursor-pointer shrink-0"
                />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">اللون الأساسي</p>
                 <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="text-xs h-7 mt-0.5"
                    dir="ltr"
                    placeholder="#8C222E"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-10 h-10 rounded-md border cursor-pointer shrink-0"
                />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">اللون الثانوي</p>
                  <Input
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="text-xs h-7 mt-0.5"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Reference Website */}
          <div>
            <Label className="flex items-center gap-1.5">
              <Globe className="h-4 w-4" />
              موقع مرجعي (اختياري)
            </Label>
            <Input
              value={referenceWebsite}
              onChange={(e) => setReferenceWebsite(e.target.value)}
              placeholder="رابط موقع ترغب في تصميم مشابه له..."
              className="mt-1 text-sm"
              dir="rtl"
            />
          </div>

          {/* Design Notes */}
          <div>
            <Label>ملاحظات التصميم <span className="text-destructive">*</span></Label>
            <Textarea
              value={designNotes}
              onChange={(e) => setDesignNotes(e.target.value)}
              placeholder="صف التصميم المطلوب، مثال: تصميم بسيط وأنيق بألوان دافئة وطابع عصري..."
              className="min-h-[100px] mt-1"
              dir="rtl"
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1">{designNotes.length}/2000</p>
          </div>

          {/* File Upload - Reference images */}
          <div>
            <Label>صور مرجعية (اختياري)</Label>
            <p className="text-xs text-muted-foreground mb-1">ارفع صور لتصاميم تعجبك أو ملفات PDF</p>
            <div className="mt-1">
              <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer hover:border-primary/50 transition-colors">
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <Upload className="h-5 w-5 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  {uploading ? "جاري الرفع..." : "اضغط لرفع صور أو ملفات PDF"}
                </span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-1.5 text-sm"
                  >
                    {file.type === "image" ? (
                      <Image className="h-4 w-4 text-blue-500 shrink-0" />
                    ) : (
                      <FileText className="h-4 w-4 text-red-500 shrink-0" />
                    )}
                    <span className="truncate flex-1">{file.name}</span>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Price info */}
          <div className="bg-muted rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">السعر مخصص لكل طلب</p>
                <p className="text-xs text-muted-foreground mt-1">
                  يتم تحديد السعر بعد مراجعة متطلبات التصميم من قبل فريقنا، وسيظهر لك السعر في لوحة التحكم للدفع
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={processing || !designNotes.trim()}
            className="w-full gap-2"
            size="lg"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                جاري الإرسال...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                إرسال طلب التصميم
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            سيتم التواصل معك لتحديد السعر بناءً على متطلبات التصميم
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDesignRequestDialog;
